import type { DateRange, MonthSeries, PerformanceStatus, RangeMetrics, SeriesSet, Territory } from '../types/dashboard';
import { perUnitRange, rangeMetrics } from './metrics';
import { territoryById } from './territory';
import { change, isCountMetric, monthValue, rangeValue, type TrendMetric } from './metricConfig';
import { performanceStatus } from './status';

export interface TerritoryRow {
  territory: Territory;
  metrics: RangeMetrics;
  series: SeriesSet;
  /** Period total (counts) or period rate. */
  value: number;
  /** Months in range the territory was active. */
  activeMonths: number;
  /** Counts: average per active month. Rates: same as value. */
  monthly: number;
  /** Counts: network avg per active territory per month (same active months). Rates: network rate. */
  benchMonthly: number;
  gap: number;
  /** Latest month in range vs prior month. */
  mom: number | null;
  /** Selected period vs same period last year. */
  yoy: number | null;
  status: PerformanceStatus;
}

/**
 * Per-territory results vs a consistent monthly benchmark:
 * for count metrics, the territory's monthly average is compared to the average of
 * (network monthly total ÷ active territories that month) over the months the territory was active.
 */
export function buildTerritoryRows(
  grouped: Map<string, SeriesSet>,
  metric: TrendMetric,
  range: DateRange,
  lyRange: DateRange | null,
  network: MonthSeries,
  networkUnits: (m: number) => number
): TerritoryRow[] {
  const count = isCountMetric(metric);
  const benchCache = new Map<number, RangeMetrics>();
  const rows: TerritoryRow[] = [];
  grouped.forEach((series, tid) => {
    const territory = territoryById.get(tid);
    if (!territory) return;
    const launch = territory.launchMonth;
    let bench = benchCache.get(launch);
    if (!bench) {
      bench = perUnitRange(network, range.start, range.end, (m) => (launch <= m ? networkUnits(m) : 0));
      benchCache.set(launch, bench);
    }
    const activeMonths = Math.max(0, range.end - Math.max(range.start, launch) + 1);
    const metrics = rangeMetrics(series.all, range.start, range.end);
    const value = rangeValue(metric, metrics);
    const benchValue = rangeValue(metric, bench);
    const monthly = count ? (activeMonths ? value / activeMonths : 0) : value;
    const benchMonthly = count ? (activeMonths ? benchValue / activeMonths : 0) : benchValue;
    const gap = change(metric, monthly, benchMonthly) ?? 0;
    const ly = lyRange && launch <= lyRange.start ? rangeValue(metric, rangeMetrics(series.all, lyRange.start, lyRange.end)) : null;
    rows.push({
      territory,
      metrics,
      series,
      value,
      activeMonths,
      monthly,
      benchMonthly,
      gap,
      mom: launch <= range.end - 1 ? change(metric, monthValue(metric, series.all, range.end), monthValue(metric, series.all, range.end - 1)) : null,
      yoy: change(metric, value, ly),
      status: performanceStatus(metric, gap, range.end - launch),
    });
  });
  return rows;
}
